import { useId, type ReactNode } from 'react';
import { ThinkingOrb } from 'thinking-orbs';
import { Tooltip } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE AGENT'S THINKING INDICATOR — `thinking-orbs`, in the accent.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Our agent when it's thinking has a really boring animation."* It was one `size-1.5` dot with
 * `animate-pulse` on it — the mockup's own typing indicator, and the least the product could draw.
 * The product owner named the replacement: `thinking-orbs`, its **Connecting** state, in the
 * selected theme's main colour.
 *
 * ⚠️ **`size={20}`, because the package ships exactly two sizes and 40 is not one of them.** From
 * its own types: *"Exactly two tuned presets ship: 64 (chat-avatar scale) and 20 (inline-text
 * scale). Each size carries its own dot count, dot size and speed tuning — they are separate
 * designs, not a scale factor."* So 40 is not a number to pass; it is a third design that does not
 * exist, and scaling a 64 down with CSS would draw a 64-tuned orb at the wrong dot density. This is
 * the fallback the request named.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE COLOUR, WHICH THE PACKAGE DOES NOT HAVE A PROP FOR.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `ThinkingOrb` takes `theme: 'auto' | 'dark' | 'light'` and nothing else: its painter draws matte
 * **greyscale** ink and mirrors the value on dark substrates. There is no colour to pass. So the
 * tint is done here, and the way it is done is the reason this file exists rather than a class on
 * the call site.
 *
 * ⚠️⚠️ **`luminanceToAlpha` → flood → `in`, and every step is load-bearing.**
 *
 *  1. `theme="dark"` pins the orb to **light ink on transparency**, whatever the app's theme is
 *     doing. That makes brightness mean *ink* rather than *substrate*, which is what step 2 needs.
 *  2. `feColorMatrix type="luminanceToAlpha"` turns that brightness into an alpha channel, so the
 *     orb's own depth shading — near dots bright, far dots dim — survives as opacity instead of
 *     being flattened. A plain recolour would lose the depth the animation is built on.
 *  3. `feFlood` + `feComposite operator="in"` paints the accent through that alpha.
 *
 * ⚠️⚠️ **`flood-color="currentColor"`, NOT a colour this file knows.** `--primary` is
 * `oklch(0.545 0.19 293)` in one theme and `#8839ef` in another, across eight of them plus dark
 * mode — reading it in JS would mean parsing colour spaces and re-reading on every theme change.
 * `currentColor` makes the browser do it: the wrapper sets `color: var(--primary)`, the filter
 * inherits it, and switching theme re-resolves with no code of ours involved.
 *
 * ⚠️ **`color-interpolation-filters="sRGB"` is not decoration.** The default is linearRGB, which
 * re-maps the luminance before step 2 reads it and draws a visibly thinner, dimmer orb.
 *
 * ⚠️ **The `<svg>` is a definition, not a picture**: zero-sized and `aria-hidden`, present only to
 * own the filter. `useId` keeps two mounted indicators from sharing an id.
 *
 * ⚠️ **Reduced motion is the package's own**, not ours: it watches `prefers-reduced-motion` and
 * paints a static frame. Nothing here should re-implement that.
 */
export function ThinkingIndicator({ label }: { readonly label: string }): ReactNode {
  const filterId = `orb-tint-${useId()}`;

  /* ⚠️ The orb carries the meaning nowhere — the label beside it is the accessible name, and
     `Working` holds the `role="status"`. A canvas announcing itself would say nothing useful. The
     kit tooltip repeats the label on hover; never a native `title`. */
  return (
    <Tooltip asChild content={label}>
      <span aria-hidden className="text-primary inline-flex shrink-0 items-center">
        <svg width="0" height="0" aria-hidden focusable="false" className="absolute">
          <defs>
            <filter id={filterId} colorInterpolationFilters="sRGB">
              <feColorMatrix type="luminanceToAlpha" result="ink" />
              <feFlood floodColor="currentColor" result="tint" />
              <feComposite in="tint" in2="ink" operator="in" />
            </filter>
          </defs>
        </svg>

        <ThinkingOrb
          state="connecting"
          size={20}
          theme="dark"
          aria-hidden
          style={{ filter: `url(#${filterId})` }}
        />
      </span>
    </Tooltip>
  );
}
